import { useEffect, useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger, TabsContainer } from '../ui'
import {
  CompanyBalanceCard,
  CompanyDetailsCard,
  CurrentMonthlyVolumesCard,
  FlaggedTransactionsCard,
  StatsWrapper,
} from '../cards/Stats'
import { SearchInput } from '../SearchInput'
import type { UserRole } from '../../types/auth'
import { LoadingPage } from '../../components/loading/pages'
import { type BadgeProps, type ButtonProps } from '../../components/carousel/BreadcrumbsCarousel'

// Company data types
export interface CompanyData {
  id: string
  company: string
  group: string
  groupId: string
  groupImageUrl?: string
  imageUrl?: string
  tradeType: 'CRYPTO' | 'CRYPTO_AND_FIAT' | 'DEACTIVATED'
  autoApprove: boolean
  parentCompanyId?: string
  parentCompanyName?: string
}

export interface CompanyStats {
  flaggedTransactions: number
  currentMonthlyVolumes: string
  previousMonthlyVolumes: string
  registrationNumber: string
  director: string
  country: string
  address: string
  website: string
}

// Breadcrumb type
export interface BreadcrumbItem {
  label: string
  onClick?: () => void
  imageUrl?: string
  fallbackImage?: React.ReactNode
  icon?: React.ReactNode
  hideImage?: boolean
  badges?: BadgeProps[]
  buttons?: ButtonProps[]
}

// Component prop types
export interface NavbarProps {
  breadcrumbs: BreadcrumbItem[]
}

export interface TabComponentProps {
  groupId?: string
  companyId?: string
  adminOnly?: boolean
  searchTerm?: string
  availableRoleFilters?: string[]
  allowedRoles?: string[]
  showUnassignUserFromGroup?: boolean
  initialStatus?: string | null
}

// Main component props
export interface CompanyPageProps {
  selectedTab: string
  setSelectedTab: (tab: string) => void
  // Route params
  companyId: string

  hideCompanyDetails?: boolean

  // Data
  company: CompanyData | null
  companyStats: CompanyStats | null
  companyLoading: boolean
  companyStatsLoading: boolean
  companyStatsError?: Error | null
  companyBalance?: { currency: string; amount: string; usdAmount: string; image: string }[] | null
  /** Pre-formatted API total (includes `$`); matches list dust floor for admin/valiant. */
  companyBalanceTotalUsdValue?: string | null

  // Components (all optional for flexibility)
  UsersTab?: React.ComponentType<TabComponentProps>
  TransactionsTab?: React.ComponentType<TabComponentProps>
  AddressesTab?: React.ComponentType<TabComponentProps>
  VolumesTab?: React.ComponentType<TabComponentProps>
  updateQueryParams?: (updates: Partial<Record<string, unknown>>) => void

  // Role configuration for UsersTab
  availableRoleFilters?: string[]
  allowedRoles?: UserRole[]
}

export function Company({
  companyId,
  hideCompanyDetails = false,
  selectedTab = 'users',
  setSelectedTab,
  company,
  companyStats,
  companyLoading,
  companyStatsLoading,
  companyStatsError,
  UsersTab,
  TransactionsTab,
  AddressesTab,
  VolumesTab,
  companyBalance,
  companyBalanceTotalUsdValue,
  updateQueryParams,
  availableRoleFilters = ['TRADER', 'GROUP_OWNER'],
  allowedRoles = ['TRADER', 'GROUP_OWNER'] as UserRole[],
}: CompanyPageProps) {
  const [transactionsInitialStatus, setTransactionsInitialStatus] = useState<string | null>(null)

  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    setSearchTerm('')
  }, [selectedTab])

  if (companyLoading || companyStatsLoading) {
    return <LoadingPage />
  }

  if (companyStatsError) {
    console.error('Company stats error:', companyStatsError)
  }

  return (
    <>
      {/* Stats Section */}
      <StatsWrapper>
        {!hideCompanyDetails && (
          <>
            <CompanyDetailsCard companyStats={companyStats} />
          </>
        )}

        <CompanyBalanceCard
          companyBalance={companyBalance || []}
          totalUsdValue={companyBalanceTotalUsdValue || '$0.00'}
        />

        <FlaggedTransactionsCard
          flaggedTransactions={companyStats?.flaggedTransactions ?? 0}
          onClick={() => {
            if (updateQueryParams)
              updateQueryParams({
                tab: 'transactions',
                status: undefined,
                type: undefined,
                isFlagged: 'true',
              })
          }}
        />
        <CurrentMonthlyVolumesCard
          currentMonthlyVolumes={companyStats?.currentMonthlyVolumes ?? '-'}
          previousMonthlyVolumes={companyStats?.previousMonthlyVolumes ?? '-'}
          onClick={() => {
            setTransactionsInitialStatus(null)
            setSelectedTab('volumes')
          }}
        />
      </StatsWrapper>

      {/* Tabs Section */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-4">
        <TabsContainer>
          <TabsList>
            {UsersTab && <TabsTrigger value="users">Users</TabsTrigger>}
            {TransactionsTab && <TabsTrigger value="transactions">Transactions</TabsTrigger>}
            {AddressesTab && <TabsTrigger value="addresses">Addresses</TabsTrigger>}
            {VolumesTab && <TabsTrigger value="volumes">Volumes</TabsTrigger>}
          </TabsList>

          <SearchInput
            placeholder="Search"
            className="w-[200px]"
            value={searchTerm}
            onChange={setSearchTerm}
          />
        </TabsContainer>

        {UsersTab && (
          <TabsContent value="users" className="space-y-4">
            <UsersTab
              companyId={companyId}
              searchTerm={searchTerm}
              groupId={company?.groupId}
              availableRoleFilters={availableRoleFilters}
              allowedRoles={allowedRoles}
            />
          </TabsContent>
        )}
        {TransactionsTab && (
          <TabsContent value="transactions" className="space-y-4">
            <TransactionsTab
              companyId={companyId}
              searchTerm={searchTerm}
              initialStatus={transactionsInitialStatus}
            />
          </TabsContent>
        )}
        {AddressesTab && (
          <TabsContent value="addresses" className="space-y-4">
            <AddressesTab companyId={companyId} />
          </TabsContent>
        )}
        {VolumesTab && (
          <TabsContent value="volumes" className="space-y-4">
            <VolumesTab companyId={companyId} searchTerm={searchTerm} />
          </TabsContent>
        )}
      </Tabs>
    </>
  )
}
